<template>
  <div>
    <v-container>
      <CRUDCriteria
        :showReset="false"
        :showSearch="false"
      >
        <template v-slot:criteriaRows>
          <v-row>
            <v-col cols="2">
              <FySelect
                v-model="fiscalYear"
                :fyRange="fiscalYearRange"
                id="check-warrant-Bal-criteria-FY-select"
                class="checkWarrantBalCriteriaFYSelect"
                @input="onChangeFy"
              />
            </v-col>
            <v-col cols="2">
              <ClarionDateControl
                v-model="fromDate"
                :isRequired="false"
                :isIsoDate="true"
                :isToggle="false"
                :label="`From Date`"
              />
            </v-col>
            <v-col cols="2">
              <ClarionDateControl
                v-model="toDate"
                :isRequired="false"
                :isIsoDate="true"
                :isToggle="false"
                :label="`To Date`"
              />
            </v-col>
            <v-col cols="2">
              <v-autocomplete
                v-model="selectedFund"
                :items="fundsList"
                item-text="fundDesc"
                item-value="_id"
                label="Fund"
                outlined
                dense
                id="check-warrant-Bal-criteria-fund-select"
                class="checkWarrantBalCriteriaFundSelect"
                @change="onChangeFund"
              />
            </v-col>
            <v-col cols="3">
              <v-btn
                color="primary"
                rounded
                @click="gatherInfo"
              >
                Gather Information
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </CRUDCriteria>
      <h2 align="center">{{ reportTitle }}</h2>
      <div align="center">
        <span class="ml-4">
          {{ `From:${fromDate}` }}
        </span>
        <span class="ml-4">
          {{ `To:${toDate}` }}
        </span>
      </div>
      <hr>
      <v-simple-table
        id="glUtilWarrantBalanceToGLReportTable"
        class="gl-util-warrant-balance-to-gl-report-table"
        dense
      >
        <template v-slot:default>
          <thead>
            <tr>
              <th>
                Date
              </th>
              <th class="text-right">
                Paid Warrants
              </th>
              <th class="text-right">
                Payments on GL
              </th>
              <th class="text-right"></th>
            </tr>
          </thead>
          <tbody>
            <template>
              <tr
                v-for="item in loadData"
                :key="item.date"
                :class="getRowClass(item)"
              >
                <td class="text-left">{{ item.date }}</td>
                <td class="text-right">{{ amountFormat(item.warAmt) }}</td>
                <td class="text-right">{{ amountFormat(item.gLBalance) }}</td>
              </tr>
            </template>
          </tbody>
        </template>
      </v-simple-table>
      <hr>
    </v-container>
  </div>
</template>
<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import ClarionDateControl from '../../common/ClarionDateControl.vue';
import FySelect from '../../common/FySelect.vue';
import CRUDCriteria from '../../common/base/crud/CRUDCriteria.vue';
import { calculateFiscalYears } from '../../../util/shared/tmc-global';
import { amountFormat } from '../../../util/shared/vue-global';
import { isBudgetedFundOrCashFund } from '../../shared/shared.util';

export default {
  name: 'CheckWarrantBalToGL',
  components: {
    CRUDCriteria,
    ClarionDateControl,
    FySelect,
  },
  data: () => ({
    fiscalYear: undefined,
    glBaseCrudKey: 'glDailyTransaction',
    fromDate: '',
    toDate: '',
    loadData: [],
    selectedFund: null,
    fundsList: [],
    fundCrudKey: 'sharedFund',
  }),
  created() {
    this.initialValue();
  },
  computed: {
    ...mapGetters(['todaysDate']),
    ...mapState({
      fiscalYearRange: (state) => {
        const registerdYears = state.glDaily.glDailyTransaction.regYears || {};
        return calculateFiscalYears(registerdYears);
      },
      funds: (state) => {
        const fundObjs = state.base.crud.items[this.fundCrudKey] || [];
        const updatedFundObj = fundObjs.map((f) => ({
          ...f,
          fundDesc: f.fund === '0000' ? f.description : `${f.fund} - ${f.description || ''}`,
        }));
        return [{ _id: null, fund: '', fundDesc: '' }, ...updatedFundObj];
      },
    }),
    // collectionColumnHeader() {
    //   const selectedFundObj = this.fundsList.find((f) => f._id === this.selectedFund);
    //   if (selectedFundObj) {
    //     return `${selectedFundObj.fundDesc} Collection`;
    //   }
    //   return 'Collection';
    // },
    reportTitle() {
      const selectedFundObj = this.fundsList.find((f) => f._id === this.selectedFund);
      if (selectedFundObj) {
        return `WARRANT COLLECTIONS FOR ${selectedFundObj.fundDesc} COMPARED TO GL BALANCE`;
      }
      return 'WARRANT COLLECTIONS COMPARED TO GL BALANCE';
    },
  },
  methods: {
    ...mapActions('glDaily/glDailyTransaction', ['loadFiscalRegYears']),
    ...mapActions('glDaily/checkWarrantBalanceToGL', ['warrantBalToGLGatherInformation']),
    ...mapActions('shared/fund', ['loadFunds']),
    amountFormat,
    async initialValue() {
      await this.loadFiscalRegYears({ baseCrudKey: this.glBaseCrudKey });
      const regYears = (this.fiscalYearRange || []);
      this.fiscalYear = (regYears[regYears.length - 1] || {}).year;
      this.fromDate = this.todaysDate;
      this.toDate = this.todaysDate;
      await this.loadFundList();
    },
    async loadFundList() {
      if (this.fiscalYear) {
        const funds = await this.loadFunds({
          criteria: {
            fiscalYear: this.fiscalYear,
            sortBy: [
              {
                column: 'fund',
                direction: 'asc',
              },
            ],
          },
          baseCrudKey: this.fundCrudKey,
        });
        const updatedFundObj = (funds || []).filter((f) => isBudgetedFundOrCashFund(f.type))
          .map((f) => ({
            ...f,
            fundDesc: f.fund === '0000' ? f.description : `${f.fund} - ${f.description || ''}`,
          }));
        this.fundsList = updatedFundObj;
      }
    },
    async onChangeFy(val) {
      this.fiscalYear = val;
      await this.loadFundList();
      this.selectedFund = null;
      this.loadData = [];
    },
    onChangeFund(val) {
      this.selectedFund = val;
      this.loadData = [];
    },
    getRowClass(item) {
      return item.matchAmtFlag ? 'accent-1' : 'red accent-1';
    },
    async gatherInfo() {
      if (!this.selectedFund) {
        alert('Please select a fund');
        return;
      }
      const criteria = {
        fiscalYear: this.fiscalYear,
        fund: this.selectedFund,
        fromDate: this.fromDate,
        toDate: this.toDate,
      };
      const result = await this.warrantBalToGLGatherInformation({ criteria });
      this.loadData = result;
    },
  },
};
</script>
